import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { OrganizationApi } from './api';
import type { 
  CreateOrganizationDto, 
  UpdateOrganizationDto,
  OrganizationFilters 
} from './types';
import { ReportFilters } from '@/lib/sdk/reports/types';

const QUERY_KEYS = {
  all: ['organizations'] as const,
  list: (filters?: OrganizationFilters) => [...QUERY_KEYS.all, 'list', filters] as const,
  listDashboards: (orgId: string) => [...QUERY_KEYS.all, 'list', orgId] as const,
  detail: (id: string) => [...QUERY_KEYS.all, 'detail', id] as const,
};

export const useOrganizations = (filters?: OrganizationFilters) => {
  return useQuery({
    queryKey: QUERY_KEYS.list(filters),
    queryFn: () => OrganizationApi.getAll(filters),
  });
};

export const useOrganization = (id: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.detail(id),
    queryFn: () => OrganizationApi.getById(id),
    enabled: !!id,
  });
};

export const useCreateOrganization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateOrganizationDto) => OrganizationApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all });
    },
  });
};

export const useUpdateOrganization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateOrganizationDto }) => 
      OrganizationApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.detail(id) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all });
    },
  });
};

export const useDeleteOrganization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => OrganizationApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all });
    },
  })
};


export const useOrgDashboards = (orgId: string, filters?: ReportFilters) => {
  return useQuery({
    queryKey: QUERY_KEYS.listDashboards(orgId),
    queryFn: () => OrganizationApi.getOrgDashboards(orgId, filters),
    enabled: !!orgId,
  });
};
